// frontend/src/features/ups/AccountDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES, CompanyStatusBadge } from './ui';

export default function AccountDashboard() {
  const sortSelectId = 'account-dashboard-sort';
  const searchInputId = 'account-dashboard-search';

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('agency');
  const [sortBy, setSortBy] = useState('username');
  const [searchTerm, setSearchTerm] = useState('');

  const statsRefreshKey = useSelector(
    (state) => state.ui?.filters?.statsRefreshKey
  );

  useEffect(() => {
    let mounted = true;

    async function fetchAccounts() {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('/ups-admin/accounts');
        if (mounted) setAccounts(response.data);
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.error || 'Error fetching accounts');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchAccounts();

    return () => {
      mounted = false;
    };
  }, [statsRefreshKey]);

  const sorted = useMemo(() => {
    const filtered = accounts.filter((acc) =>
      filter === 'ups'
        ? acc.role.startsWith('ups')
        : acc.role.startsWith('agency')
    );

    const searched = filtered.filter((acc) => {
      const q = searchTerm.toLowerCase();

      return (
        acc.username.toLowerCase().includes(q) ||
        acc.email.toLowerCase().includes(q) ||
        (acc.agency_name || '').toLowerCase().includes(q)
      );
    });

    return [...searched].sort((a, b) => {
      if (sortBy === 'agency_name') {
        return (a.agency_name || '').localeCompare(b.agency_name || '');
      }

      return (a[sortBy] || '').localeCompare(b[sortBy] || '');
    });
  }, [accounts, filter, searchTerm, sortBy]);

  const agencyCount = accounts.filter((acc) => acc.role.startsWith('agency')).length;
  const upsCount = accounts.filter((acc) => acc.role.startsWith('ups')).length;
  const filtersActive = !!searchTerm.trim();

  const toggleBlock = async (id, blocked) => {
    try {
      const res = await axios.put('/ups-admin/accounts/block', {
        userId: id,
        block: !blocked,
      });

      setAccounts((prev) => prev.map((u) => (u.id === id ? res.data : u)));
    } catch {
      alert('Error updating block status');
    }
  };

  const deleteAccount = async (id) => {
    try {
      await axios.delete(`/ups-admin/accounts/${id}`);
      setAccounts((prev) => prev.filter((u) => u.id !== id));
    } catch {
      alert('Error deleting account');
    }
  };

const tabClass = (value) =>
  `h-[46px] px-4 text-xs sm:text-sm font-semibold rounded-xl border transition
   whitespace-normal sm:whitespace-nowrap
   ${
     filter === value
       ? 'border-app-primary bg-app-primary text-white shadow-sm'
       : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
   }`;

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-app-accent">
            Administration
          </p>
          <h1 className="mt-2 text-2xl font-bold text-app-text">Manage Accounts</h1>
          <p className="mt-1 text-sm text-app-muted">
            Review agency and UPS account access, block status, and protected admin users.
          </p>
        </div>
      </div>

      {/* Filters and controls */}
      <section className="rounded-2xl border border-app-border bg-app-card p-5 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[1.2fr_2fr_0.8fr] xl:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
              Account Type
            </p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={() => setFilter('agency')} className={tabClass('agency')}>
                Agency Accounts ({agencyCount})
              </button>
              <button onClick={() => setFilter('ups')} className={tabClass('ups')}>
                UPS Accounts ({upsCount})
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor={searchInputId}
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              Search
            </label>
            <input
              id={searchInputId}
              name="accountSearch"
              type="text"
              placeholder="Search username, email, or agency…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/15"
            />
          </div>

          <div>
            <label
              htmlFor={sortSelectId}
              className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
            >
              Sort By
            </label>
            <select
              id={sortSelectId}
              name="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="mt-2 w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition focus:border-app-primary focus:ring-2 focus:ring-app-primary/15 xl:w-full"
            >
              <option value="username">Username</option>
              <option value="role">Role</option>
              {filter === 'agency' && <option value="agency_name">Agency Name</option>}
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}
      </section>

      {/* Accounts table */}
      <section className="overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-app-text">
              {filter === 'agency' ? 'Agency Accounts' : 'UPS Accounts'}
            </h2>
            <p className="mt-1 text-sm text-app-muted">
              Manage account visibility and access restrictions.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {sorted.length} shown
            {filtersActive ? ' · filters active' : ''}
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card text-sm">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {[
                  'ID',
                  'Username',
                  'Email',
                  'Role',
                  ...(filter === 'agency' ? ['Agency Name'] : []),
                  'Status',
                  'Actions',
                ].map((header) => (
                  <th
                    key={header}
                    className={`px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-app-muted ${
                      header === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {loading ? (
                <tr>
                  <td
                    colSpan={filter === 'agency' ? 7 : 6}
                    className="px-6 py-12 text-center text-sm text-app-muted"
                  >
                    Loading accounts…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td
                    colSpan={filter === 'agency' ? 7 : 6}
                    className="px-6 py-12 text-center text-sm text-app-muted"
                  >
                    {filtersActive ? 'No accounts match your search.' : 'No accounts found.'}
                  </td>
                </tr>
              ) : (
                sorted.map((acc) => (
                  <tr key={acc.id} className="transition hover:bg-app-surface">
                    <td className="whitespace-nowrap px-6 py-5 text-app-muted">{acc.id}</td>

                    <td className="whitespace-nowrap px-6 py-5 font-semibold text-app-text">
                      {acc.username || '—'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-5 text-app-text">
                      {acc.email || '—'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-5 text-app-text">
                      {acc.role || '—'}
                    </td>

                    {filter === 'agency' && (
                      <td className="whitespace-nowrap px-6 py-5 text-app-text">
                        {acc.agency_name || '—'}
                      </td>
                    )}

                    <td className="whitespace-nowrap px-6 py-5">
                      <CompanyStatusBadge tone={acc.blocked ? 'danger' : 'active'}>
                        {acc.blocked ? 'Blocked' : 'Active'}
                      </CompanyStatusBadge>
                    </td>

                    <td className="whitespace-nowrap px-6 py-5 text-right">
                      {acc.role !== 'ups_admin' ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => toggleBlock(acc.id, acc.blocked)}
                            className={COMPANY_ACTION_CLASSES.compactSecondary}
                          >
                            {acc.blocked ? 'Unblock' : 'Block'}
                          </button>

                          <button
                            onClick={() => deleteAccount(acc.id)}
                            className={COMPANY_ACTION_CLASSES.compactDestructive}
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm italic text-app-muted">Protected</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {loading ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              Loading accounts...
            </div>
          ) : sorted.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              {filtersActive ? 'No accounts match your search.' : 'No accounts found.'}
            </div>
          ) : (
            sorted.map((acc) => {
              const avatarText = (acc.username || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <DriverMobileCard
                  key={acc.id}
                  tone={acc.blocked ? 'banned' : 'internal'}
                  title={acc.username || '-'}
                  subtitle={`Account ID - ${acc.id || '-'}`}
                  avatarText={avatarText}
                  badges={[
                    <CompanyStatusBadge key="status" tone={acc.blocked ? 'danger' : 'active'}>
                      {acc.blocked ? 'Blocked' : 'Active'}
                    </CompanyStatusBadge>,
                  ]}
                  fields={[
                    ['Email', acc.email],
                    ['Role', acc.role],
                    filter === 'agency' || acc.agency_name ? ['Agency Name', acc.agency_name] : null,
                  ]}
                  actions={
                    acc.role !== 'ups_admin' ? (
                      <>
                        <button
                          onClick={() => toggleBlock(acc.id, acc.blocked)}
                          className={COMPANY_ACTION_CLASSES.compactSecondary}
                        >
                          {acc.blocked ? 'Unblock' : 'Block'}
                        </button>

                        <button
                          onClick={() => deleteAccount(acc.id)}
                          className={COMPANY_ACTION_CLASSES.compactDestructive}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <span className="text-sm italic text-app-muted">Protected</span>
                    )
                  }
                />
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
